import React from "react";
import styled from "styled-components";
import PageHeader from "../../components/PageHeader.tsx";
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  ResponsiveContainer,
} from "recharts";
import Scrollbars from "react-custom-scrollbars-2";
import { getStatistics } from "../../../services/orderService.js";
import { useQuery } from "@tanstack/react-query";
import ResumeProductTile from "../../components/ResumeProductTile.tsx";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  gap: 1rem;
  width: 100%;
  height: 100%;
`;
const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  gap: 1rem;
  div {
    flex: 1;
  }
`;
const Title = styled.p`
  margin: 0;
  color: var(--tertiary-font-color);
  font-size: 1rem;
  font-family: Verdana, sans-serif;
  padding-bottom: 0.5rem;
  width: 100%;
`;
const Content = styled.div`
  box-sizing: border-box;
  background-color: white;
  width: calc(100% - 2.5rem);
  justify-self: center;
  border-radius: 8px;
  padding: 20px;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
`;

const ChartContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  gap: 1rem;
`;

const DashboardHome: React.FC = () => {
  const { data: accountDetails } = useQuery({
    queryKey: ["statistics"],
    queryFn: () => getStatistics(),
  });

  const chart1Data = [
    {
      name: "În lucru",
      value: accountDetails?.progressOrders ?? 0,
      color: "rgb(178 133 100)",
    },
    {
      name: "Nou",
      value: accountDetails?.newOrderNumber ?? 0,
      color: "#9bc286",
    },
  ];

  const chart2Data = [
    {
      name: "Anulate",
      value: accountDetails
        ? (accountDetails?.decliendOrders * 100) /
          (accountDetails?.sendOrders + accountDetails?.decliendOrders)
        : 0,
      color: "rgb(178 133 100)",
    },
    {
      name: "Trimise",
      value: accountDetails
        ? (accountDetails?.sendOrders * 100) /
          (accountDetails?.sendOrders + accountDetails?.decliendOrders)
        : 0,
      color: "#9bc286",
    },
  ];

  const chart3Data = [
    {
      name: "Card",
      value: accountDetails
        ? (accountDetails?.cardPaymentNumber * 100) /
          accountDetails?.totalOrdersNumber
        : 0,
      color: "#9bc286",
    },
    {
      name: "Cash",
      value: accountDetails
        ? (accountDetails?.cashPaymentNumber * 100) /
          accountDetails?.totalOrdersNumber
        : 0,
      color: "rgb(178 133 100)",
    },
    {
      name: "Transfer",
      value: accountDetails
        ? (accountDetails?.transferPaymentNumber * 100) /
          accountDetails?.totalOrdersNumber
        : 0,
      color: "rgb(190 222 172)",
    },
  ];
  return (
    <Wrapper>
      <PageHeader pageTitle="Dashboard" />
      <ContentWrapper>
        <Scrollbars style={{ width: "100%", height: "100%" }}>
          <Content>
            <Title>Statistici status comenzi</Title>
            <ChartContainer>
              <PieChart width={300} height={200}>
                <Pie
                  data={chart1Data}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={20}
                  outerRadius={45}
                  fill="#9bc286"
                  label={(entry) => {
                    return `${entry.name} (${entry.value}) `;
                  }}
                >
                  {chart1Data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <text
                  x="50%"
                  y="50%"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={14}
                  fontWeight="bold"
                >
                  {accountDetails?.progressOrders} \{" "}
                  {accountDetails?.newOrderNumber +
                    accountDetails?.progressOrders}
                </text>
                <text
                  x="50%"
                  y="190"
                  textAnchor="middle"
                  fontSize={16}
                  fontWeight="normal"
                  fill="#3c3a3a"
                >
                  Comenzi active
                </text>
              </PieChart>
              <PieChart width={300} height={200}>
                <Pie
                  data={chart2Data}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={20}
                  outerRadius={45}
                  fill="#9bc286"
                  label={(entry) => {
                    return `${entry.name} (${entry.value.toFixed(2)}%) `;
                  }}
                >
                  {chart2Data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>

                <text
                  x="50%"
                  y="190"
                  textAnchor="middle"
                  fontSize={16}
                  fontWeight="normal"
                  fill="#3c3a3a"
                >
                  Comenzi finalizate
                </text>
              </PieChart>
              <PieChart width={300} height={200}>
                <Pie
                  data={chart3Data}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={20}
                  outerRadius={45}
                  fill="#9bc286"
                  label={(entry) => {
                    return `${entry.name} (${entry.value.toFixed(2)}%) `;
                  }}
                >
                  {chart3Data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>

                <text
                  x="50%"
                  y="190"
                  textAnchor="middle"
                  fontSize={16}
                  fontWeight="normal"
                  fill="#3c3a3a"
                >
                  Opțiuni de plată
                </text>
              </PieChart>
            </ChartContainer>
          </Content>

          <Content>
            <Title>Vânzările din ultimele 6 luni</Title>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={accountDetails?.sales}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="totalSales" stroke="#9bc286" />
              </LineChart>
            </ResponsiveContainer>
          </Content>

          <Content>
            <Title>Top 3 produse</Title>
            <ChartContainer>
              {accountDetails?.bestProducts.map((product) => {
                return (
                  <ResumeProductTile
                    key={product.productID}
                    productID={product.productID}
                    productName={product.productName}
                    price={product.price}
                    discount={product.discount}
                    image={product.imageUrls[0]}
                  />
                );
              })}
            </ChartContainer>
          </Content>
        </Scrollbars>
      </ContentWrapper>
    </Wrapper>
  );
};

export default DashboardHome;
